/** Which subset of fields the browser narrows to. 'all' shows every field in its group; the
 *  other two narrow every group down to fields used on this chart, or fields this reader pinned. */
export type FieldBrowserFilter = 'all' | 'used' | 'pinned';

export type FieldBrowserPreferences = {
  filter: FieldBrowserFilter;
  /** Group labels (PanelFieldGroup) the reader has collapsed. Absent means expanded. */
  collapsed: Array<string>;
  pinned: Array<string>;
  search: string;
};

const FILTERS: Array<FieldBrowserFilter> = ['all', 'used', 'pinned'];

/** The old shape's per-kind categories ('measure' | 'dimension' | 'date') no longer choose a
 *  filter: the field list groups by kind now instead of narrowing to one kind at a time, so those
 *  three collapse onto 'all' rather than being lost. */
const LEGACY_CATEGORY_TO_FILTER: Record<string, FieldBrowserFilter> = {
  all: 'all',
  used: 'used',
  pinned: 'pinned',
  measure: 'all',
  dimension: 'all',
  date: 'all',
};

function toStringArray(value: unknown): Array<string> {
  return Array.isArray(value)
    ? [...new Set<string>(value.filter((entry): entry is string => typeof entry === 'string'))]
    : [];
}

export function parseFieldBrowserPreferences(raw: string | null): FieldBrowserPreferences {
  const fallback: FieldBrowserPreferences = { collapsed: [], filter: 'all', pinned: [], search: '' };
  try {
    const value = JSON.parse(raw ?? 'null');
    if (!value || typeof value !== 'object') {
      return fallback;
    }
    const filter: FieldBrowserFilter = FILTERS.includes(value.filter)
      ? value.filter
      : (typeof value.category === 'string' && LEGACY_CATEGORY_TO_FILTER[value.category]) || 'all';
    return {
      collapsed: toStringArray(value.collapsed),
      filter,
      pinned: toStringArray(value.pinned),
      search: typeof value.search === 'string' ? value.search : '',
    };
  } catch {
    return fallback;
  }
}

export function readFieldBrowserPreferences(datasetId?: string): FieldBrowserPreferences {
  try {
    return parseFieldBrowserPreferences(
      datasetId ? window.localStorage.getItem(`le-lens:field-browser:${datasetId}`) : null,
    );
  } catch {
    return parseFieldBrowserPreferences(null);
  }
}

export function writeFieldBrowserPreferences(
  datasetId: string | undefined,
  preferences: FieldBrowserPreferences,
): void {
  if (!datasetId) {
    return;
  }
  try {
    window.localStorage.setItem(`le-lens:field-browser:${datasetId}`, JSON.stringify(preferences));
  } catch {
    /* Browser preferences are optional; chart editing remains available. */
  }
}
